// src/services/eventVisualService.js

import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Derive the hero thumbnail, teaser, and a small gallery using consistent rules:
 *   1) Prefer event.thumbnail.url (event-level)
 *   2) Otherwise, prefer latest manual asset thumbnail (createdAt desc)
 *   3) Otherwise, latest Azure thumb from submissionSaleId
 *   4) Gradient fallback (returns null so caller shows gradient)
 *
 * Also returns:
 *   - teaserUrl: first teaserVideo.url found (latest-first)
 *   - contentThumbs: up to maxThumbs gallery thumbs using same manual→Azure order
 *
 * Notes:
 * - We order by createdAt desc when possible for deterministic picks.
 * - If some docs lack createdAt, Firestore will still order; if index is missing,
 *   remove orderBy or create the index.
 */
export async function fetchEventVisuals(eventOrId, options = {}) {
  const { maxThumbs = 6, preferEventThumbnail = true } = options;

  const event =
    typeof eventOrId === "object" && eventOrId !== null ? eventOrId : null;
  const eventId =
    typeof eventOrId === "string" ? eventOrId : event?.id || event?.eventId;

  if (!eventId) throw new Error("fetchEventVisuals: missing eventId");

  // 1) Event-level thumbnail takes absolute precedence
  if (preferEventThumbnail && event?.thumbnail?.url) {
    // Still gather teaser & gallery from content for completeness
    const { teaserUrl, contentThumbs } = await gatherFromContent(eventId, {
      maxThumbs,
    });
    return {
      thumbnailUrl: event.thumbnail.url,
      teaserUrl,
      contentThumbs,
    };
  }

  // 2) Otherwise derive from content
  return gatherFromContent(eventId, { maxThumbs });
}

async function gatherFromContent(eventId, { maxThumbs }) {
  // Try to order by createdAt desc for deterministic "latest"
  let snap;
  try {
    const q = query(
      collection(db, "events", eventId, "content"),
      orderBy("createdAt", "desc")
    );
    snap = await getDocs(q);
  } catch {
    // Fallback if no index/field
    snap = await getDocs(collection(db, "events", eventId, "content"));
  }

  const manualThumbs = [];
  const azureThumbs = [];
  let teaserUrl = null;

  snap.forEach((d) => {
    const item = d.data();
    // teaser (first one wins, latest-first when ordered)
    if (!teaserUrl && item?.teaserVideo?.url) teaserUrl = item.teaserVideo.url;

    // gallery buckets
    if (item?.thumbnail?.url) {
      manualThumbs.push(item.thumbnail.url);
    } else if (item?.submissionSaleId) {
      azureThumbs.push(
        `https://swayer.blob.core.windows.net/${item.submissionSaleId}-pub/_thumb_large.jpg`
      );
    }
  });

  // Hero pick: manual first, then azure (both already latest-first if ordered)
  const thumbnailUrl =
    manualThumbs[0] || azureThumbs[0] || null; // null => caller shows gradient

  // Gallery: merge, de-dupe, and cap
  const gallery = [...manualThumbs, ...azureThumbs]
    .filter(Boolean)
    .filter((url, i, arr) => arr.indexOf(url) === i) // de-dupe
    .slice(0, maxThumbs);

  return { thumbnailUrl, teaserUrl, contentThumbs: gallery };
}
